import { useState } from 'react'
import { Link } from 'react-router-dom'
import { API_BASE_URL } from '../lib/config'

// Creates the first admin, before any session exists to authorise it. The
// creation secret is typed here and sent as a header — this is the only place
// in the UI that ever handles it. An existing admin adding a colleague uses the
// Admins page instead, where the server supplies the secret itself.
export function BootstrapPage() {
	const [form, setForm] = useState({ email: '', password: '', name: '', secret: '' })
	const [state, setState] = useState<{ error?: string; done?: boolean; busy?: boolean }>({})

	function set(key: keyof typeof form) {
		return (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }))
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault()
		setState({ busy: true })
		try {
			const res = await fetch(`${API_BASE_URL}/api/admin/auth/sign-up/email`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json', 'x-admin-creation-secret': form.secret },
				body: JSON.stringify({ email: form.email, password: form.password, name: form.name }),
			})
			const data = await res.json().catch(() => ({}))
			if (!res.ok) {
				setState({ error: (data as { message?: string }).message ?? 'Could not create the admin' })
				return
			}
			setState({ done: true })
		} catch {
			setState({ error: 'Could not reach the server' })
		}
	}

	if (state.done) {
		return (
			<div className="flex min-h-full items-center justify-center p-5">
				<div className="card w-full max-w-sm p-6 text-center">
					<h1 className="font-display text-lg font-semibold">Admin created</h1>
					<p className="mt-2 text-sm text-ink-soft">You can now sign in with those credentials.</p>
					<Link to="/login" className="btn-primary mt-5 w-full">
						Go to sign in
					</Link>
				</div>
			</div>
		)
	}

	return (
		<div className="flex min-h-full items-center justify-center p-5">
			<form onSubmit={handleSubmit} className="card w-full max-w-sm p-6">
				<h1 className="font-display text-lg font-semibold">Create an admin</h1>
				<p className="mb-5 mt-1 text-sm text-ink-soft">Requires the admin creation secret.</p>

				<label className="label" htmlFor="name">
					Name
				</label>
				<input id="name" className="input mb-4" value={form.name} onChange={set('name')} required />

				<label className="label" htmlFor="bs-email">
					Email
				</label>
				<input
					id="bs-email"
					type="email"
					className="input mb-4"
					value={form.email}
					onChange={set('email')}
					autoComplete="username"
					required
				/>

				<label className="label" htmlFor="bs-password">
					Password
				</label>
				<input
					id="bs-password"
					type="password"
					className="input mb-4"
					value={form.password}
					onChange={set('password')}
					autoComplete="new-password"
					minLength={12}
					required
				/>
				<p className="-mt-3 mb-4 text-xs text-ink-faint">At least 12 characters.</p>

				<label className="label" htmlFor="secret">
					Admin creation secret
				</label>
				<input id="secret" type="password" className="input mb-4" value={form.secret} onChange={set('secret')} required />

				{state.error && (
					<p role="alert" className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
						{state.error}
					</p>
				)}

				<button type="submit" className="btn-primary w-full" disabled={state.busy}>
					{state.busy ? 'Creating…' : 'Create admin'}
				</button>
				<Link to="/login" className="mt-3 block text-center text-sm text-ink-soft hover:underline">
					Back to sign in
				</Link>
			</form>
		</div>
	)
}
